import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Printer, ListChecks, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { useAppSettings } from "@/context/AppSettings";
import { useAuth } from "@/context/AuthContext";
import { fetchTreatments, deleteTreatment } from "@/lib/api";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";

export default function PriceList() {
  const { currency } = useAppSettings();
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setTreatments(await fetchTreatments()); } catch (e) { toast.error("Couldn't load your Price List"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (t) => {
    try {
      await deleteTreatment(t.id);
      setTreatments((list) => list.filter((x) => x.id !== t.id));
      toast.success("Treatment removed");
    } catch (e) {
      toast.error("Couldn't remove treatment");
    }
  };

  const totals = useMemo(() => {
    const price = treatments.reduce((s, t) => s + (t.result?.priceInclGst || 0), 0);
    const profit = treatments.reduce((s, t) => s + ((t.result?.priceExGst || 0) - (t.result?.breakEven || 0)), 0);
    return { price, profit };
  }, [treatments]);

  const cur = (t) => t.result?.currency || currency;

  const catLabel = (c) => (c || "Other").replace(/[-_]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const grouped = useMemo(() => {
    const g = {};
    treatments.forEach((t) => { const k = t.category || "Other"; (g[k] = g[k] || []).push(t); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [treatments]);
  const colCount = isAdmin ? 6 : 3;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Price List</h1>
          <p className="text-muted-foreground mt-2">{isAdmin ? "Your saved, profitable treatment menu." : "Your clinic's treatment menu and prices."}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-lg" onClick={() => window.print()} data-testid="print-pricelist-button">
            <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
          </Button>
          {isAdmin && (
            <Link to="/treatments">
              <Button className="rounded-lg" data-testid="new-treatment-button">
                <FlaskConical className="h-4 w-4 mr-1.5" /> New Treatment
              </Button>
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground py-16 text-center">Loading…</div>
      ) : treatments.length === 0 ? (
        <Card className="rounded-2xl border bg-card p-12 shadow-sm text-center" data-testid="pricelist-empty">
          <ListChecks className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="font-heading font-semibold text-lg">No treatments yet</h3>
          <p className="text-muted-foreground mt-1 mb-4">{isAdmin ? "Build and save your first treatment to start your menu." : "Your clinic hasn't published any treatment prices yet."}</p>
          {isAdmin && <Link to="/treatments"><Button className="rounded-lg" data-testid="empty-new-treatment">Build a treatment</Button></Link>}
        </Card>
      ) : (
        <>
          {isAdmin && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <Card className="rounded-2xl border bg-card p-4 shadow-sm">
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Treatments</div>
                <div className="font-heading font-bold text-2xl mt-1" data-testid="pricelist-count">{treatments.length}</div>
              </Card>
              <Card className="rounded-2xl border bg-card p-4 shadow-sm">
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Menu value</div>
                <div className="font-heading font-bold text-2xl mt-1 tabular-nums">{fmtCurrency(totals.price, currency)}</div>
              </Card>
              <Card className="rounded-2xl border bg-card p-4 shadow-sm">
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Total profit</div>
                <div className="font-heading font-bold text-2xl mt-1 tabular-nums text-primary">{fmtCurrency(totals.profit, currency)}</div>
              </Card>
            </div>
          )}

          <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="pricelist-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[480px]">
                <thead>
                  <tr className="text-left font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-medium">Treatment</th>
                    <th className="py-2 px-3 font-medium">Device</th>
                    {isAdmin && <th className="py-2 px-3 font-medium text-right">Break-even</th>}
                    {isAdmin && <th className="py-2 px-3 font-medium text-right">Margin</th>}
                    <th className="py-2 px-3 font-medium text-right">Price</th>
                    {isAdmin && <th className="py-2 pl-3 font-medium text-right"></th>}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([cat, items]) => (
                    <React.Fragment key={cat}>
                      <tr className="bg-muted/40" data-testid={`pricelist-group-${cat}`}>
                        <td colSpan={colCount} className="py-2 px-1 font-heading font-semibold text-sm">
                          {catLabel(cat)} <span className="text-muted-foreground font-normal text-xs">· {items.length}</span>
                        </td>
                      </tr>
                      {items.map((t) => (
                        <tr key={t.id} className="border-b border-border/50 last:border-0" data-testid={`pricelist-row-${t.id}`}>
                          <td className="py-3 pr-3 pl-3">
                            <div className="font-medium">{t.name}</div>
                            <div className="text-xs text-muted-foreground">{t.area}</div>
                          </td>
                          <td className="py-3 px-3 text-muted-foreground">{t.device_name}</td>
                          {isAdmin && <td className="py-3 px-3 text-right tabular-nums">{fmtCurrency(t.result?.breakEven || 0, cur(t))}</td>}
                          {isAdmin && <td className="py-3 px-3 text-right"><Badge variant="secondary" className="tabular-nums">{t.margin}%</Badge></td>}
                          <td className="py-3 px-3 text-right tabular-nums font-heading font-bold text-primary">{fmtCurrency(t.result?.priceInclGst || 0, cur(t))}</td>
                          {isAdmin && (
                            <td className="py-3 pl-3 text-right">
                              <button onClick={() => onDelete(t)} className="text-muted-foreground hover:text-destructive" data-testid={`pricelist-delete-${t.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
